import { Injectable } from '@angular/core';
import { User, UserManager, UserManagerSettings } from 'oidc-client';
import { Subject, BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as _ from 'lodash';
import { ProfileManagerService } from './profilemanager.service';
import { Profile } from '../models/profile';
import { GenerationOwner } from "../models/generationowner";
import { HttpClient } from '@angular/common/http';
import { CacheService } from './cache.service';
import { FineGrainAuthorization } from '../models/fine-grain-authorization';



export function getSettings(): UserManagerSettings {
  return {
    authority: environment.authority,
    client_id: environment.client_id,
    redirect_uri: `${globalThis.location.origin}/signin-callback`,
    post_logout_redirect_uri: `${globalThis.location.origin}/signout-callback`,
    response_type: 'code',
    scope: environment.scope,
    filterProtocolClaims: true,
    loadUserInfo: true,
    automaticSilentRenew: true,
    acr_values: 'anmlogin',
    accessTokenExpiringNotificationTime: 120,
    silent_redirect_uri: `${globalThis.location.origin}/silent-callback`,
    includeIdTokenInSilentRenew: false,
    silentRequestTimeout: 120000,
  };
}

function safeParseJSON<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _userManager = new UserManager(getSettings());
  private _user: User | null;
  private readonly _loginChangedSubject = new Subject<boolean>();
  private readonly _userLoadedSubject = new Subject<User | null>();
  _profile: any;

  loginChanged = this._loginChangedSubject.asObservable();
  userLoaded = this._userLoadedSubject.asObservable();

  generationOwnerUpdatedSubject = new Subject();
  generationOwnerUpdated = this.generationOwnerUpdatedSubject.asObservable();
  _userGenerationOwners: any[];
  _userRoles: any[];
  _userGenerationOwnersAccessSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  _userRolesAccessSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  userGenerationOwnersAccess = this._userGenerationOwnersAccessSubject.asObservable();
  userRolesAccess = this._userRolesAccessSubject.asObservable();
  userSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  selectedGenOwnerSubject: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);
  selectedGenOwnerRoleSubject: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);
  selectedGenOwner: any;
  selectedGenOwnerRole: any[];
  selectedGenOwnerHasCOGFUnit: boolean | null = null;

  _PortalFunctionalitiesAccess: any;
  _PortalFunctionalitiesAccessSubject = new Subject<any>();
  PortalFunctionalitiesAccess = this._PortalFunctionalitiesAccessSubject.asObservable();

  constructor(private readonly profileManager: ProfileManagerService, private readonly http: HttpClient, private readonly cache: CacheService) {
    this._userManager.events.addSilentRenewError(() => {
      this.Logout();
    });

    this._userManager.events.addUserSignedOut(() => {
      this.Logout();
    });

    this._userManager.events.addUserLoaded((u) => {
      this._userLoadedSubject.next(u);
    });

  }

  Login(): Promise<any> {
    return this._userManager.signinRedirect().catch((err) => {
      console.error('Login Error:', err);
    });
  }

  Logout(): Promise<any> {
    return this._userManager.signoutRedirect();
  }

  isLoggedIn(): Promise<boolean> {
    return this._userManager.getUser().then((user) => {
      this.refreshUserData(user);
      const userCurrent = !!user && !user.expired;
      if (this._user !== user) {
        this._loginChangedSubject.next(userCurrent);
      }
      this._user = user;
      return userCurrent;
    });
  }

  refreshUserData(currentUser: any) {
    this._profile = [];
    if (currentUser?.profile) {
      const p = currentUser.profile;
      // safe access with defaults when fields missing
      this._profile.push(
        new Profile(
          p?.firstname ?? '',
          p?.telephoneNumber ?? '',
          p?.auditTrackingId ?? '',
          p?.isverified ?? false,
          p?.role ?? '',
          p?.azp ?? '',
          p?.email ?? '',
          p?.lastname ?? '',
          p?.lastLoginTime ?? '',
          p?.pwdChangedTime ?? '',
          p?.generationId ?? '',
          p?.stateProvince ?? '',
          p?.anmRoleMap ?? []
        )
      );
      this.profileManager.profile = this._profile;
    }
  }

  getUser(): Promise<User | null> {
    return this._userManager.getUser().then((user: any) => {
      if (user) {
        this.getandupdateUserRoles(user);
        this.SetLoggedinUser(user);
      }
      return this._user;
    });

  }

  LoginComplete() {
    return this._userManager.signinRedirectCallback().then((user) => {
      this._user = user;
      this._loginChangedSubject.next(!!user && !user.expired);


      this.getandupdateUserRoles(user);
      this.SetLoggedinUser(user);
      return user;
    });
  }

  LogOutComplete() {
    this._user = null;
    this._userLoadedSubject.next(null);
    this._loginChangedSubject.next(false);
    this.setSelectedGenerationOwner();
    this.setSelectedGenerationOwnerRole();
    this.generationOwnerUpdatedSubject.next(null);
    this._userGenerationOwnersAccessSubject.next(null);
    this._userRolesAccessSubject.next(null);
    this.userSubject.next(null);
    this.setPortalFunctionalitiesAccess();
    this.selectedGenOwnerSubject.next(null);
    this.selectedGenOwnerRoleSubject.next(null);
    this.profileManager.resetProfileManager();
    this.cache.clearCache();
    return this._userManager
      .signoutRedirectCallback()
      .then(() => {
        this._userManager.removeUser();
      })
      .catch((error) => this.handleError(error));
  }

  handleError(error: any) {
    console.error('Problem with ending the session: ', error);
  }

  getAccessToken() {
    let currentUser;
    return this._userManager.getUser().then((user) => {
      currentUser = user;
      return currentUser?.access_token;
    });
  }

  resetUser() {
    this._user = null;
    this._userLoadedSubject.next(null);
    this._loginChangedSubject.next(false);
    this.profileManager.resetProfileManager();
    return this._userManager
      .signoutRedirectCallback()
      .then(() => {
        this._userManager.removeUser();
      })
      .catch((error) => this.handleError(error));
  }

  getCurrentUser() {

    if (this._user) {
      this.SetLoggedinUser(this._user);
      this.getandupdateUserRoles(this._user);
      return this._user;
    }
    else {
      return this.getUser();
    }
  }


  setSelectedGenerationOwner(genOwner?: any): void {

    if (!genOwner) {
      this.selectedGenOwner = null;
      sessionStorage.removeItem('selectedGenerationOwner');
      this.selectedGenOwnerSubject.next(false);
      this.generationOwnerUpdatedSubject.next(false);
    }
    else {
      sessionStorage.setItem('selectedGenerationOwner', JSON.stringify(genOwner));
      this.selectedGenOwner = JSON.stringify(genOwner);
      this.selectedGenOwnerSubject.next(true);
      this.generationOwnerUpdatedSubject.next(true);
    }

  }

  getSelectedGenerationOwner(): GenerationOwner | null {
    let genOwner = this.selectedGenOwner ? this.selectedGenOwner : sessionStorage.getItem('selectedGenerationOwner') || '';
    if (genOwner !== '') {
      return safeParseJSON<GenerationOwner | null>(genOwner, null);
    }
    return null;
  }

  getSelectedGenerationOwnerID(): any {
    let genOwner = this.getSelectedGenerationOwner();
    return genOwner?.ownerId;
  }

  setSelectedGenerationOwnerHasCOGFUnit(hasCOGFUnit?: any): void {
    if (hasCOGFUnit === undefined || hasCOGFUnit === null) {
      this.selectedGenOwnerHasCOGFUnit = null;
      sessionStorage.removeItem('selectedGenerationOwnerHasCOGFUnit');
    } else {
      const toStore = (typeof hasCOGFUnit === 'boolean') ? String(hasCOGFUnit) : String(!!safeParseJSON<any>(hasCOGFUnit, false));
      sessionStorage.setItem('selectedGenerationOwnerHasCOGFUnit', toStore);
      this.selectedGenOwnerHasCOGFUnit = toStore === 'true';
    }
  }

  getSelectedGenerationOwnerHasCOGFUnit(): boolean {
    if (this.selectedGenOwnerHasCOGFUnit !== null) return this.selectedGenOwnerHasCOGFUnit;
    const stored = sessionStorage.getItem('selectedGenerationOwnerHasCOGFUnit');
    if (!stored) return false;
    return stored === 'true';
  }

  setSelectedGenerationOwnerRole(genOwnerRole?: any): void {
    if (!genOwnerRole) {
      this.selectedGenOwnerRole = [];
      sessionStorage.removeItem('selectedGenerationOwnerRole');
      this.selectedGenOwnerRoleSubject.next(false);
    }
    else {
      // ensure we store a string
      const toStore = typeof genOwnerRole === 'string' ? genOwnerRole : JSON.stringify(genOwnerRole);
      sessionStorage.setItem('selectedGenerationOwnerRole', toStore);
      this.selectedGenOwnerRole = genOwnerRole;
      this.selectedGenOwnerRoleSubject.next(true);
    }

  }

  getSelectedGenerationOwnerRole() {

    let selectedRole: any[] = [];
    const stored = sessionStorage.getItem('selectedGenerationOwnerRole');
    if (stored) {
      // try parse, fallback to raw string
      const parsed = safeParseJSON<any>(stored, stored);
      if (Array.isArray(parsed)) selectedRole.push(...parsed);
      else selectedRole.push(parsed);
    } else if (this.selectedGenOwnerRole) {
      // if nothing in storage and local prop exists, return that
      return this.selectedGenOwnerRole;
    }

    let role: any = this.selectedGenOwnerRole ? this.selectedGenOwnerRole : selectedRole;

    return role;
  }


  getaccess() {
    return this.cache.Cache<any>('ANM_CurrentAccesses', () =>
      this.http.get<any>("../../../assets/data/Rolebased_Functionality_Access.json")
    );
  }

  getPortalFunctioanalityAccesses(functionality?: string, role?: any): FineGrainAuthorization {
    let functionalityAccess: FineGrainAuthorization = new FineGrainAuthorization();

    let roles: any = [];
    let RoleAccess: any = [];

    if (role == undefined) {
      const anmRoleMap = this._user?.profile?.['anmRoleMap'];
      let userRole = Array.isArray(anmRoleMap)
        ? anmRoleMap.filter(
          (lst: any, i: any, arr: any) => arr.findIndex((t: any) => t.generationOwner === lst.generationOwner && t.status === 'Active') === i
        )
        : [];

      let internal_roles = (Array.isArray(userRole) ? userRole : []).filter(
        (a: any) =>
          (a.role != 'anmExternalAdmin' && a.role != 'anmExternalViewOnly') && (a.status === 'Active')
      );

      let isInternalUser = internal_roles && internal_roles.length > 0;
      if (isInternalUser)
        role = internal_roles.map((x: any) => x.role);
      else
        role = this.getSelectedGenerationOwnerRole();
    }

    roles = role ? role : this.getCurrentUserRolesAccess();
    RoleAccess = this.getFunctionalityAccesses(functionality, role?.toString());


    (Array.isArray(roles) ? roles : [roles])?.forEach((a: any) => {

      if (a === 'anmExternalAdmin' || a === 'anmInternalAdmin') functionalityAccess.isAdmin = true;

      if ((a == 'anmExternalAdmin' || a == 'anmExternalViewOnly')) {
        functionalityAccess.isAmerenInternal = false;
        functionalityAccess.isExternal = true;
      }
      else {
        functionalityAccess.isAmerenInternal = true;
        functionalityAccess.isExternal = false;
      }

      if (a === 'anmInternalAdmin') functionalityAccess.isAmerenAdmin = true;

      if (a === 'anmExternalAdmin') functionalityAccess.isExternalAdmin = true;

      if (a === 'anmRebateApprover') functionalityAccess.isRebateApprover = true;

      if (a === 'anmIPAViewOnly') functionalityAccess.isIPAUser = true;

    });


    functionalityAccess.allowUsersList = this.RoleContains(RoleAccess, 'READ_USERS');
    functionalityAccess.allowEditUser = this.RoleContains(RoleAccess, 'EDIT_USERS');
    functionalityAccess.allowEnableDisableUsers = this.RoleContains(RoleAccess, 'ENABLE_DISABLE_USERS');
    functionalityAccess.allowLockUnlockUsers = this.RoleContains(RoleAccess, 'LOCK_UNLOCK_USERS');

    functionalityAccess.allowGenerationOwnersList = this.RoleContains(RoleAccess, 'READ_GENERATION_OWNER_LIST');
    functionalityAccess.allowCreateGenerationOwner = this.RoleContains(RoleAccess, 'CREATE_GENERATION_OWNERS');
    functionalityAccess.allowEditGenerationOwner = this.RoleContains(RoleAccess, 'UPDATE_GENERATION_OWNERS');
    functionalityAccess.allowReadGenerationOwnerBankingInfo = this.RoleContains(RoleAccess, 'READ_GENERATION_OWNERS_BANK_INFO');
    functionalityAccess.allowEditGenerationOwnerBankingInfo = this.RoleContains(RoleAccess, 'UPDATE_GENERATION_OWNERS_BANKINFO');

    functionalityAccess.allowGenerationUnitsList = this.RoleContains(RoleAccess, 'READ_GENERATION_UNIT_LIST');
    functionalityAccess.allowCreateGenerationUnit = this.RoleContains(RoleAccess, 'CREATE_GENERATION_UNIT');
    functionalityAccess.allowEditGenerationUnit = this.RoleContains(RoleAccess, 'EDIT_GENERATION_UNIT');
    functionalityAccess.allowRemoveGenerationUnit = this.RoleContains(RoleAccess, 'REMOVE_GENERATION_UNIT');

    functionalityAccess.allowSubscriberList = this.RoleContains(RoleAccess, 'READ_SUBSCRIBER_LIST');
    functionalityAccess.allowCreateSubscriber = this.RoleContains(RoleAccess, 'CREATE_SUBSCRIBERS');
    functionalityAccess.allowEditSubscriber = this.RoleContains(RoleAccess, 'EDIT_SUBSCRIBERS');
    functionalityAccess.allowEnableDisableSubscribers = this.RoleContains(RoleAccess, 'ENABLE_DISABLE_SUBSCRIBERS');

    functionalityAccess.allowRebateList = this.RoleContains(RoleAccess, 'READ_REBATE_LIST');
    functionalityAccess.allowRebateDetail = this.RoleContains(RoleAccess, 'READ_REBATE_DETAIL');
    functionalityAccess.allowEditRebate = this.RoleContains(RoleAccess, 'EDIT_REBATE');

    functionalityAccess.allowReportsList = this.RoleContains(RoleAccess, 'READ_REPORT_LIST');
    functionalityAccess.allowGenerationOwnerReport = this.RoleContains(RoleAccess, 'GENERATION_OWNER_REPORT');
    functionalityAccess.allowGenerationUnitSubscriberBillAccountReport = this.RoleContains(RoleAccess, 'GENERATION_UNIT_SUBSCRIBER_BILL_ACCOUNT_REPORT');
    functionalityAccess.allowGenerationUnitDailyOutputReport = this.RoleContains(RoleAccess, 'GENERATION_UNIT_OUTPUT_REPORT');
    functionalityAccess.allowGenerationUnitSubscriberContractReport = this.RoleContains(RoleAccess, 'GENERATION_UNIT_SUBSCRIBER_CONTRACT_REPORT');
    functionalityAccess.allowGenerationUnitSubscriberDailyAllocationReport = this.RoleContains(RoleAccess, 'GENERATION_UNIT_SUBSCRIBER_ALLOCATION_REPORT');
    functionalityAccess.allowSubscriberDailyAllocationReport = this.RoleContains(RoleAccess, 'SUBSCRIBER_DAILY_ALLOCATION_REPORT');
    functionalityAccess.allowGenerationUnitAllSubscriberBillAccountReport = this.RoleContains(RoleAccess, 'GENERATION_UNIT_ALL_SUBSCRIBER_BILL_ACCOUNT_REPORT');
    functionalityAccess.allowIPAGenerationUnitSubscriberContractReport = this.RoleContains(RoleAccess, 'IPA_GENERATION_UNIT_SUBSCRIBER_CONTRACT_REPORT');
    functionalityAccess.allowRegulatoryComplianceReport = this.RoleContains(RoleAccess, 'REGULATORY_COMPLIANCE_REPORT');
    functionalityAccess.allowGenerationUnitBillAccountSubscriptionRateReport = this.RoleContains(RoleAccess, 'GENERATION_UNIT_BILL_ACCOUNT_SUBSCRIPTION_RATE_REPORT');
    functionalityAccess.allowGenerationUnitBillAccountSubscriptionReport = this.RoleContains(RoleAccess, 'GENERATION_UNIT_BILL_ACCOUNT_SUBSCRIPTION_REPORT');
    functionalityAccess.allowCOGFParticipantBillAccountReport = this.RoleContains(RoleAccess, 'COGF_PARTICIPANT_BILL_ACCOUNT_REPORT');
    functionalityAccess.allowCOGFAllParticipantBillAccountReport = this.RoleContains(RoleAccess, 'COGF_ALL_PARTICIPANT_BILL_ACCOUNT_REPORT');

    return functionalityAccess;

  }

  RoleContains(rolesAcess: any[], roles: any): boolean {
    let hasAccess: boolean = false;
    if (Array.isArray(rolesAcess) && rolesAcess.some(r => roles == r))
      hasAccess = true;
    return hasAccess
  }

  getFunctionalityAccesses(functionality?: string, role?: string) {
    let Roleaccess: any[] = [];
    let currentAccess: any[] = [];

    // prefer in-memory value
    const inMemory = this.getCurrentPortalFunctionalitiesAccess();
    if (Array.isArray(inMemory) && inMemory.length > 0) {
      currentAccess = inMemory;
    } else {
      // try to read from session storage safely
      const stored = sessionStorage.getItem('PortalFunctionalitiesAccess');
      const parsed = safeParseJSON<any[]>(stored, []);
      if (Array.isArray(parsed) && parsed.length > 0) {
        currentAccess = parsed;
        this._PortalFunctionalitiesAccess = parsed;
      } else {
        // trigger a one-shot fetch to populate cache for next calls (take(1) ensures subscription completes)
        this.getaccess().pipe(take(1)).subscribe(res => {
          const fetched = res?.Role_Functioanlty_Accesses ?? [];
          this.setPortalFunctionalitiesAccess(fetched);
        }, () => { /* ignore fetch errors here to avoid breaking synchronous callers */ });
      }
    }

    if (!Array.isArray(currentAccess)) currentAccess = [];

    Roleaccess.push(...currentAccess
      .filter((a: any) =>
        functionality ? (Array.isArray(a.Roles) && a.Roles.includes(role) && a.Functioanlity === functionality) : (Array.isArray(a.Roles) && a.Roles.includes(role)))
      .map((x: any) => x.Code || ''));

    return Roleaccess;
  }


  getandupdateUserRoles(usr: User): void {
    let roles: any = [];
    let genOwners: any = [];
    const govRoleMap = usr?.profile?.['anmRoleMap'];

    if (!Array.isArray(govRoleMap)) {
      this._userGenerationOwners = [];
      this._userRoles = [];
      this._userGenerationOwnersAccessSubject.next([]);
      this._userRolesAccessSubject.next([]);
      return;
    }

    govRoleMap.forEach((gen: any) => {
      const genOwnerid = gen?.generationOwner?.toString()?.trim() ?? '';

      if (Array.isArray(gen?.role)) {
        gen.role.forEach((rol: string) => {
          if (rol) {
            roles.push(rol.toString());
          }
        })
      } else if (gen?.role) {
        roles.push(gen.role.toString());
      }

      if (genOwnerid)
        genOwners.push(genOwnerid);

    });

    //push unique gen owner ids to array
    genOwners = genOwners.filter((n: any, i: any) => genOwners.indexOf(n) === i);

    //push unique roles to array
    roles = roles.filter((n: any, i: any) => roles.indexOf(n) === i);

    this._userGenerationOwners = genOwners;
    this._userRoles = roles;

    this._userGenerationOwnersAccessSubject.next(genOwners);
    this._userRolesAccessSubject.next(roles);

  }

  getCurrentUserGenerationOwnersAccess() {
    return this._userGenerationOwners;
  }
  getCurrentUserRolesAccess() {
    return this._userRoles;
  }

  getCurrentPortalFunctionalitiesAccess() {
    return this._PortalFunctionalitiesAccess;
  }

  SetLoggedinUser(usr: User): void {
    this._user = usr;
    this.userSubject.next(usr);
  }

  public getLoggedinUser() {
    if (this._user) {
      return this._user;
    }
    return null;
  }

  setPortalFunctionalitiesAccess(access?: any): void {

    if (access) {
      try {
        sessionStorage.setItem('PortalFunctionalitiesAccess', JSON.stringify(access));
      } catch {
        // fallback: store as string
        sessionStorage.setItem('PortalFunctionalitiesAccess', String(access));
      }
      this._PortalFunctionalitiesAccess = access;
    }
    else {
      this._PortalFunctionalitiesAccess = [];
      sessionStorage.setItem('PortalFunctionalitiesAccess', JSON.stringify([]));
    }

  }

  getPortalFunctionalitiesAccess() {

    // return in-memory when available
    if (this._PortalFunctionalitiesAccess && Array.isArray(this._PortalFunctionalitiesAccess)) {
      return this._PortalFunctionalitiesAccess;
    }

    const stored = sessionStorage.getItem('PortalFunctionalitiesAccess');
    const parsed = safeParseJSON<any[]>(stored, []);
    return parsed;
  }

}